import type { MainMenuItemType } from '#base/core/types/MainMenuItemType';
import { Injury, InjuryEnum } from '#fysio/core/enums/InjuryEnum';
import type { UrlType } from '#base/core/types/UrlType';

export default (menuType: 'desktop' | 'mobile' = 'desktop'): MainMenuItemType[] => {
	const items: MainMenuItemType[] = [];

	if (menuType === 'mobile') {
		items.push({
			text: 'Home',
			to: {
				title: 'Fysio Hier homepage',
				url: '/',
				newTab: false,
			},
		});
	}

	items.push({
		text: 'Fysiotherapie',
		to: {
			title: 'Fysiotherapie Uden',
			url: '/fysiotherapie',
			newTab: false,
		},
	});

	items.push({
		text: 'Behandelingen',
		to: {
			title: 'Fysio behandelingen',
			url: '/fysiotherapie/behandelingen',
			newTab: false,
		},
		submenu: {
			items: [
				{
					text: 'Shockwave therapie',
					description: 'Shockwave therapie is een geavanceerde behandelvorm voor pees- en spierblessures.',
					icon: 'fa-wave-square',
					to: {
						title: 'Fysio behandeling shockwave therapie',
						url: '/fysiotherapie/behandelingen/shockwave-therapie',
					},
				},
				{
					text: 'Dry needling',
					description: 'Dry needling verhelpt triggerpoints in de spieren om spierspanning weg te nemen.',
					icon: 'fa-arrows-to-dot',
					to: {
						title: 'Fysio behandeling dry needling',
						url: '/fysiotherapie/behandelingen/dry-needling',
					},
				},
				{
					text: 'GLA:D',
					description: 'Een bewezen aanpak voor artrose om pijnvrij te bewegen en je gewrichten te ontlasten.',
					icon: 'fa-hand-holding-medical',
					to: {
						title: 'GLA:D Programma',
						url: '/fysiotherapie/behandelingen/glad-programma',
					},
				},
				{
					text: 'Medical Taping',
					description: 'Medical taping ondersteund de spieren en gewrichten met speciale elastische tape.',
					icon: 'fa-dumbbell',
					to: {
						title: 'Fysio behandeling medical taping / sport taping',
						url: '/fysiotherapie/behandelingen/medical-taping-sporttape',
					},
				},
				{
					text: 'Sportrevalidatie',
					description: 'Sportrevalidatie is gericht op het herstellen van atleten tijdens en na blessures.',
					icon: 'fa-tape',
					to: {
						title: 'Fysio behandeling sportrevalidatie',
						url: '/fysiotherapie/behandelingen/sportrevalidatie',
					},
				},
				{
					text: 'Pre- en postoperatieve begeleiding',
					description: 'Pre- en postoperatieve begeleiding is een cruciaal proces vooraf én na je operatie.',
					icon: 'fa-heart-circle-plus',
					to: {
						title: 'Pre- en postoperatieve begeleiding',
						url: '/fysiotherapie/behandelingen/pre-post-operatieve-begeleiding',
					},
				},
				{
					text: 'Hardloopanalyse',
					description: 'Tijdens een hardloopanalyse observeert de therapeut de manier waarop je rent.',
					icon: 'fa-person-running',
					to: {
						title: 'Hardloopanalyse',
						url: '/fysiotherapie/behandelingen/hardloopanalyse',
					},
				},
				{
					text: 'Sportmassage',
					description: 'Een gespecialiseerde vorm van massage om atleten en sporters te ondersteunen.',
					icon: 'fa-hands',
					to: {
						title: 'Sportmassage',
						url: '/fysiotherapie/behandelingen/sportmassage',
					},
				},
			],
			banner: {
				title: 'Loop niet langer rond met je klachten dan nodig!',
				image: {
					src: '/img/fysio/hielpijn_01.png',
					alt: 'Last van de hiel',
					width: 826,
					height: 466,
				},
				to: {
					url: '?d=afspraak-inplannen',
					title: 'Plan een afspraak fysiotherapie',
				},
				buttonText: 'Plan een afspraak',
			},
		},
	});

	items.push({
		text: 'Fysieke klachten',
		to: {
			title: 'Veelvoorkomende fysieke klachten',
			url: '/fysiotherapie/pijnklachten-fysieke-beperkingen',
			newTab: false,
		},
		submenu: {
			items: [
				{
					text: 'Enkelklachten',
					description: 'Enkelklachten uiten zich vaak als pijn, zwelling, stijfheid of instabiliteit en kunnen lopen, sporten of traplopen beperken.',
					image: {
						src: Injury.init(InjuryEnum.AnklePain).iconLight,
						alt: 'Last van de enkel',
					},
					to: Injury.init(InjuryEnum.AnklePain).pageUrl as UrlType,
				},
				{
					text: 'Hielklachten',
					description: 'De klacht komt meestal van onder of net voor de hiel en kan stekend, brandend of zeurend aanvoelen.',
					image: {
						src: Injury.init(InjuryEnum.HeelPain).iconLight,
						alt: 'Last van de hiel',
					},
					to: Injury.init(InjuryEnum.HeelPain).pageUrl as UrlType,
				},
				{
					text: 'Knieklachten',
					description: 'Knieklachten vertonen verschillende symptomen en ongemakken. De klachten variëren in intensiteit en duur.',
					image: {
						src: Injury.init(InjuryEnum.KneePain).iconLight,
						alt: 'Last van de knie',
					},
					to: Injury.init(InjuryEnum.KneePain).pageUrl as UrlType,
				},
				{
					text: 'Heupklachten',
					description: 'Symptomen zijn pijn aan de lies, buitenzijde van de heup, stijfheid, een klikkend of blokkerend gevoel of moeite met bewegen.',
					image: {
						src: Injury.init(InjuryEnum.HipPain).iconLight,
						alt: 'Last van de schouder',
					},
					to: Injury.init(InjuryEnum.HipPain).pageUrl as UrlType,
				},
				{
					text: 'Zwangerschapsklachten',
					description: 'Fysieke en hormonale verschuivingen kunnen verschillende lichamelijke klachten en ongemakken veroorzaken.',
					image: {
						src: Injury.init(InjuryEnum.PregnancyInjury).iconLight,
						alt: 'Zwangerschapsklachten',
					},
					to: Injury.init(InjuryEnum.PregnancyInjury).pageUrl as UrlType,
				},
				{
					text: 'Nekklachten',
					description: 'Nekklachten worden vaak ervaren als pijn, stijfheid en ongemak in het gebied van de nek en schouders.',
					image: {
						src: Injury.init(InjuryEnum.NeckPain).iconLight,
						alt: 'Last van de nek',
					},
					to: Injury.init(InjuryEnum.NeckPain).pageUrl as UrlType,
				},
				{
					text: 'Rugklachten',
					description: 'Rugpijn is een veelvoorkomende klacht die veel invloed kan hebben op je dagelijkse activiteiten.',
					image: {
						src: Injury.init(InjuryEnum.BackPain).iconLight,
						alt: 'Last van de rug',
					},
					to: Injury.init(InjuryEnum.BackPain).pageUrl as UrlType,
				},
				{
					text: 'Schouderklachten',
					description: 'Schouderklachten komen veel voor en kunnen je dagelijkse bezigheden flink beperken.',
					image: {
						src: Injury.init(InjuryEnum.ShoulderPain).iconLight,
						alt: 'Last van de schouder',
					},
					to: Injury.init(InjuryEnum.ShoulderPain).pageUrl as UrlType,
				},
			],
			banner: {
				title: 'Laat een blessure jouw sportprestaties niet langer in de weg zitten!',
				image: {
					src: '/img/fysio/tenniselleboog_03.png',
					alt: 'Last van een tenniselleboog',
					width: 826,
					height: 466,
				},
				to: {
					url: '?d=afspraak-inplannen',
					title: 'Plan een afspraak fysiotherapie',
				},
				buttonText: 'Plan een afspraak',
			},
		},
	});

	items.push({
		text: 'Team',
		to: {
			title: 'Het Fysio Hier team',
			url: '/team',
			newTab: false,
		},
	});

	return items;
};
